import { Pathfinder } from '../maps/Pathfinder';
import { Vector2 } from '../utils/Vector2';
import { MapDefinition, MapProp, MapTileType, MapCoordinate } from './MapTypes';
import { MapStorage } from './MapStorage';

interface MapEditorOptions {
  onClose?: () => void;
  onTestMap?: (map: MapDefinition) => void;
}

interface SelectionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface MapSnapshot {
  grid: MapTileType[][];
  spawns: MapCoordinate[];
  goal: MapCoordinate | null;
  props: MapProp[];
  metadata: MapDefinition['metadata'];
  name: string;
  author: string;
  difficultyTags: string;
}

type ToolMode = 'brush' | 'eraser' | 'spawn' | 'goal' | 'select' | 'prop';

const randomId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
};

export class MapEditor {
  private readonly gridSize = 20;
  private readonly canvasSize = 1080;
  private readonly maxSpawnCount = 4;
  private readonly historyLimit = 75;

  private root: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private toolbar: HTMLDivElement;
  private sidebar: HTMLDivElement;
  private statusBar: HTMLDivElement;
  private nameInput: HTMLInputElement;
  private authorInput: HTMLInputElement;
  private difficultyInput: HTMLInputElement;
  private descriptionInput: HTMLTextAreaElement;
  private scriptsInput: HTMLTextAreaElement;
  private savedMapsSelect: HTMLSelectElement;
  private enemyTypeSelect: HTMLSelectElement;
  private propSelect: HTMLSelectElement;
  private undoButton: HTMLButtonElement;
  private redoButton: HTMLButtonElement;
  private moveButtons: Record<'up' | 'down' | 'left' | 'right', HTMLButtonElement>;

  private tileSize: number;
  private grid: MapTileType[][];
  private spawns: MapCoordinate[];
  private goal: MapCoordinate | null;
  private props: MapProp[] = [];
  private currentTool: ToolMode = 'brush';
  private brushSize = 1;
  private dragging = false;
  private draggingSpawnIndex: number | null = null;
  private draggingGoal = false;
  private selection: SelectionRect | null = null;
  private selectionData: MapTileType[][] | null = null;
  private selectStart: MapCoordinate | null = null;
  private undoStack: MapSnapshot[] = [];
  private redoStack: MapSnapshot[] = [];
  private previewPaths: Vector2[][] = [];
  private currentMapId: string | null = null;
  private currentPropType = 'tree';
  private pathfinder = new Pathfinder();

  constructor(private options: MapEditorOptions = {}) {
    this.ensureStyles();
    this.grid = this.createEmptyGrid();
    this.spawns = [{ x: 1, y: 1 }];
    this.goal = { x: 18, y: 18 };
    this.tileSize = this.canvasSize / this.gridSize;
    this.root = document.createElement('div');
    this.root.className = 'map-editor-root';
    this.root.style.display = 'none';
    document.body.appendChild(this.root);

    this.canvas = document.createElement('canvas');
    this.canvas.width = this.canvasSize;
    this.canvas.height = this.canvasSize;
    this.canvas.className = 'map-editor-canvas';
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to acquire canvas context for MapEditor');
    }
    this.ctx = ctx;

    this.toolbar = document.createElement('div');
    this.toolbar.className = 'map-editor-toolbar';

    this.sidebar = document.createElement('div');
    this.sidebar.className = 'map-editor-sidebar';

    this.statusBar = document.createElement('div');
    this.statusBar.className = 'map-editor-status';
    this.statusBar.textContent = 'Map Editor ready.';

    this.moveButtons = {
      up: document.createElement('button'),
      down: document.createElement('button'),
      left: document.createElement('button'),
      right: document.createElement('button'),
    };

    this.nameInput = document.createElement('input');
    this.authorInput = document.createElement('input');
    this.difficultyInput = document.createElement('input');
    this.descriptionInput = document.createElement('textarea');
    this.scriptsInput = document.createElement('textarea');
    this.savedMapsSelect = document.createElement('select');
    this.enemyTypeSelect = document.createElement('select');
    this.propSelect = document.createElement('select');
    this.undoButton = document.createElement('button');
    this.redoButton = document.createElement('button');

    this.buildLayout();
    this.attachCanvasEvents();
    this.refreshSavedMaps();
    this.pushHistory();
    this.render();
  }

  show(): void {
    this.root.style.display = 'flex';
    this.render();
    this.statusBar.textContent = 'Map Editor active.';
  }

  hide(): void {
    this.root.style.display = 'none';
    this.options.onClose?.();
  }

  private ensureStyles(): void {
    if (document.getElementById('map-editor-styles')) return;
    const style = document.createElement('style');
    style.id = 'map-editor-styles';
    style.textContent = `
      .map-editor-root {
        position: fixed;
        inset: 0;
        background: rgba(5, 5, 10, 0.95);
        color: #fff;
        font-family: 'Courier New', monospace;
        z-index: 12000;
        display: flex;
        flex-direction: column;
      }
      .map-editor-main {
        flex: 1;
        display: flex;
        gap: 16px;
        padding: 16px;
        overflow: hidden;
      }
      .map-editor-canvas {
        background: #111;
        border: 2px solid #2c2c2c;
        border-radius: 8px;
        max-width: min(1080px, 60vw);
        height: auto;
      }
      .map-editor-toolbar, .map-editor-sidebar {
        background: rgba(20, 20, 30, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 8px;
        padding: 12px;
        max-height: calc(100vh - 120px);
        overflow-y: auto;
      }
      .map-editor-toolbar {
        width: 300px;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .map-editor-sidebar {
        width: 320px;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .map-editor-section {
        border: 1px solid rgba(255, 255, 255, 0.07);
        border-radius: 6px;
        padding: 10px;
      }
      .map-editor-section h4 {
        margin: 0 0 8px;
        font-size: 18px;
        color: #ffd54f;
      }
      .map-editor-section button {
        margin: 2px;
        padding: 6px 8px;
        font-family: inherit;
        background: #2e2e40;
        color: #fff;
        border: 1px solid #555;
        border-radius: 4px;
        cursor: pointer;
      }
      .map-editor-section button.active {
        background: #00a86b;
        border-color: #00c47a;
      }
      .map-editor-status {
        padding: 8px 16px;
        background: rgba(0, 0, 0, 0.8);
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        font-size: 17px;
      }
      .map-editor-metadata input,
      .map-editor-metadata textarea,
      .map-editor-section select {
        width: 100%;
        margin-bottom: 6px;
        padding: 6px;
        border-radius: 4px;
        border: 1px solid #444;
        background: #111;
        color: #fff;
        font-family: inherit;
      }
      .map-editor-close {
        margin-top: 12px;
        padding: 8px;
        background: #8e2020;
        border: 1px solid #ff5c5c;
        border-radius: 4px;
        cursor: pointer;
        color: #fff;
      }
    `;
    document.head.appendChild(style);
  }

  private buildLayout(): void {
    const header = document.createElement('div');
    header.style.display = 'flex';
    header.style.justifyContent = 'space-between';
    header.style.alignItems = 'center';
    header.style.padding = '12px 16px';
    header.innerHTML = `<h3 style="margin:0;font-size:23px;">Map Editor</h3>`;

    const closeBtn = document.createElement('button');
    closeBtn.textContent = 'Back to Menu';
    closeBtn.className = 'map-editor-close';
    closeBtn.onclick = () => this.hide();
    header.appendChild(closeBtn);

    const mainArea = document.createElement('div');
    mainArea.className = 'map-editor-main';
    mainArea.appendChild(this.toolbar);
    mainArea.appendChild(this.canvas);
    mainArea.appendChild(this.sidebar);

    this.root.appendChild(header);
    this.root.appendChild(mainArea);
    this.root.appendChild(this.statusBar);

    this.buildToolbar();
    this.buildSidebar();
  }

  private buildToolbar(): void {
    this.toolbar.innerHTML = '';

    const toolsSection = this.createSection('Tools');
    const tools: Array<{ label: string; tool: ToolMode }> = [
      { label: 'Brush', tool: 'brush' },
      { label: 'Eraser', tool: 'eraser' },
      { label: 'Spawn', tool: 'spawn' },
      { label: 'Goal', tool: 'goal' },
      { label: 'Select', tool: 'select' },
      { label: 'Props', tool: 'prop' },
    ];
    tools.forEach(({ label, tool }) => {
      const btn = document.createElement('button');
      btn.textContent = label;
      btn.classList.toggle('active', this.currentTool === tool);
      btn.onclick = () => {
        this.currentTool = tool;
        this.updateToolButtons();
      };
      btn.dataset['tool'] = tool;
      toolsSection.appendChild(btn);
    });
    this.toolbar.appendChild(toolsSection);

    const brushSection = this.createSection('Brush Size');
    const brushSlider = document.createElement('input');
    brushSlider.type = 'range';
    brushSlider.min = '1';
    brushSlider.max = '5';
    brushSlider.value = this.brushSize.toString();
    brushSlider.oninput = () => {
      this.brushSize = parseInt(brushSlider.value, 10);
    };
    brushSection.appendChild(brushSlider);
    this.toolbar.appendChild(brushSection);

    const undoRedoSection = this.createSection('History');
    this.undoButton.textContent = 'Undo';
    this.redoButton.textContent = 'Redo';
    this.undoButton.onclick = () => this.undo();
    this.redoButton.onclick = () => this.redo();
    undoRedoSection.appendChild(this.undoButton);
    undoRedoSection.appendChild(this.redoButton);
    this.toolbar.appendChild(undoRedoSection);

    const multiSelectSection = this.createSection('Selection Move');
    this.moveButtons.up.textContent = '▲';
    this.moveButtons.down.textContent = '▼';
    this.moveButtons.left.textContent = '◀';
    this.moveButtons.right.textContent = '▶';
    this.moveButtons.up.onclick = () => this.moveSelection(0, -1);
    this.moveButtons.down.onclick = () => this.moveSelection(0, 1);
    this.moveButtons.left.onclick = () => this.moveSelection(-1, 0);
    this.moveButtons.right.onclick = () => this.moveSelection(1, 0);
    Object.values(this.moveButtons).forEach((btn) => {
      btn.disabled = true;
      multiSelectSection.appendChild(btn);
    });
    this.toolbar.appendChild(multiSelectSection);

    const testingSection = this.createSection('Testing Utilities');
    this.enemyTypeSelect.innerHTML = '';
    ['ground', 'flying', 'ghost', 'boss'].forEach((type) => {
      const option = document.createElement('option');
      option.value = type;
      option.textContent = type;
      this.enemyTypeSelect.appendChild(option);
    });
    testingSection.appendChild(this.enemyTypeSelect);

    const spawnTestBtn = document.createElement('button');
    spawnTestBtn.textContent = 'Simulate Spawn';
    spawnTestBtn.onclick = () => this.simulateSpawn(this.enemyTypeSelect.value);
    testingSection.appendChild(spawnTestBtn);

    const addGoldBtn = document.createElement('button');
    addGoldBtn.textContent = '+500 Gold (Sim)';
    addGoldBtn.onclick = () => this.flashStatus('Gold simulation: +500 (for testing).');
    testingSection.appendChild(addGoldBtn);

    const highlightPathBtn = document.createElement('button');
    highlightPathBtn.textContent = 'Highlight Path';
    highlightPathBtn.onclick = () => this.previewPathsForMap();
    testingSection.appendChild(highlightPathBtn);

    const validateBtn = document.createElement('button');
    validateBtn.textContent = 'Validate Map';
    validateBtn.onclick = () => this.validateMap();
    testingSection.appendChild(validateBtn);

    this.toolbar.appendChild(testingSection);
  }

  private buildSidebar(): void {
    this.sidebar.innerHTML = '';

    const metadataSection = this.createSection('Metadata');
    this.nameInput.placeholder = 'Map Name';
    this.authorInput.placeholder = 'Author';
    this.difficultyInput.placeholder = 'Difficulty tags (comma separated)';
    this.descriptionInput.placeholder = 'Description / notes';
    this.scriptsInput.placeholder = 'Future scripts / hooks JSON';
    metadataSection.appendChild(this.nameInput);
    metadataSection.appendChild(this.authorInput);
    metadataSection.appendChild(this.difficultyInput);
    metadataSection.appendChild(this.descriptionInput);
    metadataSection.appendChild(this.scriptsInput);
    this.sidebar.appendChild(metadataSection);

    const propsSection = this.createSection('Props Library');
    this.propSelect.innerHTML = '';
    ['tree', 'rock', 'torch', 'statue'].forEach((prop) => {
      const option = document.createElement('option');
      option.value = prop;
      option.textContent = prop;
      this.propSelect.appendChild(option);
    });
    this.propSelect.onchange = () => {
      this.currentPropType = this.propSelect.value;
    };
    propsSection.appendChild(this.propSelect);
    propsSection.appendChild(document.createTextNode('Use Props tool to place.'));
    this.sidebar.appendChild(propsSection);

    const pathSection = this.createSection('Path Preview');
    const previewBtn = document.createElement('button');
    previewBtn.textContent = 'Run A* Preview';
    previewBtn.onclick = () => this.previewPathsForMap();
    pathSection.appendChild(previewBtn);
    const testBtn = document.createElement('button');
    testBtn.textContent = 'Test Map';
    testBtn.onclick = () => this.testMap();
    pathSection.appendChild(testBtn);
    this.sidebar.appendChild(pathSection);

    const saveSection = this.createSection('Save / Load');
    const saveBtn = document.createElement('button');
    saveBtn.textContent = 'Save Map';
    saveBtn.onclick = () => this.saveMap();
    const exportBtn = document.createElement('button');
    exportBtn.textContent = 'Export JSON';
    exportBtn.onclick = () => this.exportMap();
    const importBtn = document.createElement('button');
    importBtn.textContent = 'Import JSON';
    importBtn.onclick = () => this.importMap();
    this.savedMapsSelect.style.width = '100%';
    const loadBtn = document.createElement('button');
    loadBtn.textContent = 'Load Selected';
    loadBtn.onclick = () => {
      if (this.savedMapsSelect.value) {
        this.loadMap(this.savedMapsSelect.value);
      }
    };
    const deleteBtn = document.createElement('button');
    deleteBtn.textContent = 'Delete Selected';
    deleteBtn.onclick = () => {
      if (this.savedMapsSelect.value) {
        MapStorage.delete(this.savedMapsSelect.value);
        this.refreshSavedMaps();
        this.flashStatus('Map deleted.');
      }
    };
    saveSection.appendChild(this.savedMapsSelect);
    saveSection.appendChild(loadBtn);
    saveSection.appendChild(saveBtn);
    saveSection.appendChild(exportBtn);
    saveSection.appendChild(importBtn);
    saveSection.appendChild(deleteBtn);
    this.sidebar.appendChild(saveSection);
  }

  private createSection(title: string): HTMLDivElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'map-editor-section';
    const heading = document.createElement('h4');
    heading.textContent = title;
    wrapper.appendChild(heading);
    return wrapper;
  }

  private updateToolButtons(): void {
    const buttons = this.toolbar.querySelectorAll<HTMLButtonElement>('button[data-tool]');
    buttons.forEach((button) => {
      button.classList.toggle('active', button.dataset['tool'] === this.currentTool);
    });
  }

  private attachCanvasEvents(): void {
    this.canvas.addEventListener('pointerdown', (event) => this.handlePointerDown(event));
    this.canvas.addEventListener('pointermove', (event) => this.handlePointerMove(event));
    this.canvas.addEventListener('pointerup', () => this.handlePointerUp());
    this.canvas.addEventListener('pointerleave', () => this.handlePointerUp());
  }

  private handlePointerDown(event: PointerEvent): void {
    const tile = this.getTileFromPointer(event);
    if (!tile) return;
    this.dragging = true;

    if (this.currentTool === 'brush') {
      this.pushHistory();
      this.paintAt(tile.x, tile.y);
    } else if (this.currentTool === 'eraser') {
      this.pushHistory();
      this.eraseAt(tile.x, tile.y);
    } else if (this.currentTool === 'spawn') {
      const spawnIndex = this.findSpawnAt(tile.x, tile.y);
      if (spawnIndex >= 0) {
        this.draggingSpawnIndex = spawnIndex;
      } else if (this.spawns.length < this.maxSpawnCount) {
        this.pushHistory();
        this.spawns.push(tile);
        this.draggingSpawnIndex = this.spawns.length - 1;
      }
    } else if (this.currentTool === 'goal') {
      if (this.goal && this.goal.x === tile.x && this.goal.y === tile.y) {
        this.draggingGoal = true;
      } else {
        this.pushHistory();
        this.goal = { ...tile };
        this.draggingGoal = true;
      }
    } else if (this.currentTool === 'prop') {
      this.pushHistory();
      this.togglePropAt(tile.x, tile.y);
    } else if (this.currentTool === 'select') {
      this.selectStart = tile;
      this.selection = { x: tile.x, y: tile.y, width: 1, height: 1 };
      this.selectionData = null;
    }

    this.render();
  }

  private handlePointerMove(event: PointerEvent): void {
    if (!this.dragging) return;
    const tile = this.getTileFromPointer(event);
    if (!tile) return;

    if (this.currentTool === 'brush') {
      this.paintAt(tile.x, tile.y);
    } else if (this.currentTool === 'eraser') {
      this.eraseAt(tile.x, tile.y);
    } else if (this.currentTool === 'spawn' && this.draggingSpawnIndex !== null) {
      this.spawns[this.draggingSpawnIndex] = tile;
    } else if (this.currentTool === 'goal' && this.draggingGoal) {
      this.goal = { ...tile };
    } else if (this.currentTool === 'select' && this.selectStart) {
      const width = tile.x - this.selectStart.x + 1;
      const height = tile.y - this.selectStart.y + 1;
      this.selection = {
        x: Math.min(this.selectStart.x, tile.x),
        y: Math.min(this.selectStart.y, tile.y),
        width: Math.abs(width),
        height: Math.abs(height),
      };
    }

    this.render();
  }

  private handlePointerUp(): void {
    if (!this.dragging) return;
    this.dragging = false;
    this.draggingSpawnIndex = null;
    this.draggingGoal = false;

    if (this.currentTool === 'select' && this.selection && !this.selectionData) {
      this.captureSelectionTiles();
    }

    this.render();
  }

  private paintAt(x: number, y: number): void {
    for (let dy = -this.brushSize + 1; dy < this.brushSize; dy++) {
      for (let dx = -this.brushSize + 1; dx < this.brushSize; dx++) {
        const tx = x + dx;
        const ty = y + dy;
        if (this.isWithinGrid(tx, ty)) {
          this.grid[ty][tx] = 'wall';
        }
      }
    }
  }

  private eraseAt(x: number, y: number): void {
    if (this.isWithinGrid(x, y)) {
      this.grid[y][x] = 'empty';
    }
  }

  private togglePropAt(x: number, y: number): void {
    const existingIndex = this.props.findIndex((prop) => prop.position.x === x && prop.position.y === y);
    if (existingIndex >= 0) {
      this.props.splice(existingIndex, 1);
    } else {
      this.props.push({
        id: randomId(),
        type: this.currentPropType,
        position: { x, y },
      });
    }
  }

  private findSpawnAt(x: number, y: number): number {
    return this.spawns.findIndex((spawn) => spawn.x === x && spawn.y === y);
  }

  private captureSelectionTiles(): void {
    if (!this.selection) return;
    const { x, y, width, height } = this.selection;
    this.selectionData = [];
    for (let row = 0; row < height; row++) {
      const rowData: MapTileType[] = [];
      for (let col = 0; col < width; col++) {
        rowData.push(this.grid[y + row][x + col]);
        this.grid[y + row][x + col] = 'empty';
      }
      this.selectionData.push(rowData);
    }
    Object.values(this.moveButtons).forEach((btn) => (btn.disabled = false));
    this.render();
  }

  private moveSelection(dx: number, dy: number): void {
    if (!this.selection || !this.selectionData) return;
    const newX = this.selection.x + dx;
    const newY = this.selection.y + dy;
    if (newX < 0 || newY < 0 || newX + this.selection.width > this.gridSize || newY + this.selection.height > this.gridSize) {
      this.flashStatus('Selection move blocked by bounds.');
      return;
    }
    this.pushHistory();
    this.selection.x = newX;
    this.selection.y = newY;
    for (let row = 0; row < this.selection.height; row++) {
      for (let col = 0; col < this.selection.width; col++) {
        this.grid[newY + row][newX + col] = this.selectionData[row][col];
      }
    }
    this.render();
  }

  private undo(): void {
    if (this.undoStack.length <= 1) return;
    const current = this.undoStack.pop();
    if (!current) return;
    this.redoStack.push(current);
    const snapshot = this.undoStack[this.undoStack.length - 1];
    this.restoreSnapshot(snapshot);
    this.render();
    this.flashStatus('Undo.');
  }

  private redo(): void {
    const snapshot = this.redoStack.pop();
    if (!snapshot) return;
    this.undoStack.push(snapshot);
    this.restoreSnapshot(snapshot);
    this.render();
    this.flashStatus('Redo.');
  }

  private restoreSnapshot(snapshot: MapSnapshot): void {
    this.grid = snapshot.grid.map((row) => [...row]);
    this.spawns = snapshot.spawns.map((spawn) => ({ ...spawn }));
    this.goal = snapshot.goal ? { ...snapshot.goal } : null;
    this.props = snapshot.props.map((prop) => ({ ...prop, position: { ...prop.position } }));
    this.nameInput.value = snapshot.name;
    this.authorInput.value = snapshot.author;
    this.difficultyInput.value = snapshot.difficultyTags;
    this.descriptionInput.value = snapshot.metadata.description ?? '';
    this.scriptsInput.value = snapshot.metadata.scripts?.join('\n') ?? '';
    this.selection = null;
    this.selectionData = null;
    Object.values(this.moveButtons).forEach((btn) => (btn.disabled = true));
  }

  private previewPathsForMap(): void {
    if (!this.goal) {
      this.flashStatus('Place a goal before previewing.');
      return;
    }
    const grid = this.toPathfinderGrid();
    this.previewPaths = [];
    for (const spawn of this.spawns) {
      const path = this.pathfinder.findPath(
        new Vector2(spawn.x, spawn.y),
        new Vector2(this.goal.x, this.goal.y),
        grid,
      );
      if (path.length > 0) {
        this.previewPaths.push(path.map((tile) => Vector2.fromTileCentered(tile.x, tile.y, this.tileSize)));
      }
    }
    if (this.previewPaths.length === 0) {
      this.flashStatus('No valid path found. Map is blocked.');
    } else {
      this.flashStatus(`Path preview generated for ${this.previewPaths.length} spawn(s).`);
    }
    this.render();
  }

  private validateMap(): void {
    if (!this.goal) {
      this.flashStatus('Validation failed: goal missing.');
      return;
    }
    if (this.spawns.length === 0) {
      this.flashStatus('Validation failed: at least one spawn required.');
      return;
    }
    const grid = this.toPathfinderGrid();
    const invalidSpawns = this.spawns.filter((spawn) => {
      const path = this.pathfinder.findPath(
        new Vector2(spawn.x, spawn.y),
        new Vector2(this.goal!.x, this.goal!.y),
        grid,
      );
      return path.length === 0;
    });
    if (invalidSpawns.length > 0) {
      this.flashStatus(`Validation failed: ${invalidSpawns.length} spawn(s) have no route.`);
    } else {
      this.flashStatus('Validation successful: All spawns can reach the goal.');
    }
  }

  private toPathfinderGrid(): any[][] {
    const grid: any[][] = this.grid.map((row) =>
      row.map((tile) => ({
        type: tile,
      }))
    );
    this.spawns.forEach((spawn) => {
      if (grid[spawn.y] && grid[spawn.y][spawn.x]) {
        grid[spawn.y][spawn.x] = { type: 'spawn' };
      }
    });
    if (this.goal && grid[this.goal.y] && grid[this.goal.y][this.goal.x]) {
      grid[this.goal.y][this.goal.x] = { type: 'goal' };
    }
    return grid;
  }

  private saveMap(): void {
    if (!this.goal) {
      this.flashStatus('Cannot save: goal missing.');
      return;
    }
    const map: MapDefinition = {
      id: this.currentMapId ?? randomId(),
      name: this.nameInput.value || 'Untitled Map',
      author: this.authorInput.value || 'Unknown',
      difficultyTags: this.difficultyInput.value
        ? this.difficultyInput.value.split(',').map((tag) => tag.trim()).filter(Boolean)
        : [],
      grid: this.grid.map((row) => [...row]),
      spawns: this.spawns.map((spawn) => ({ ...spawn })),
      goal: this.goal ? { ...this.goal } : null,
      props: this.props.map((prop) => ({ ...prop, position: { ...prop.position } })),
      metadata: {
        description: this.descriptionInput.value,
        scripts: this.scriptsInput.value ? this.scriptsInput.value.split('\n').filter(Boolean) : [],
        createdAt: Date.now(),
        updatedAt: Date.now(),
      },
    };
    const saved = MapStorage.save(map);
    this.currentMapId = saved.id;
    this.refreshSavedMaps(saved.id);
    this.flashStatus('Map saved locally.');
  }

  private loadMap(id: string): void {
    const map = MapStorage.load(id);
    if (!map) {
      this.flashStatus('Failed to load map.');
      return;
    }
    this.currentMapId = map.id;
    this.grid = map.grid.map((row) => [...row]);
    this.spawns = map.spawns.map((spawn) => ({ ...spawn }));
    this.goal = map.goal ? { ...map.goal } : null;
    this.props = map.props.map((prop) => ({ ...prop, position: { ...prop.position } }));
    this.nameInput.value = map.name;
    this.authorInput.value = map.author;
    this.difficultyInput.value = map.difficultyTags.join(', ');
    this.descriptionInput.value = map.metadata.description ?? '';
    this.scriptsInput.value = map.metadata.scripts?.join('\n') ?? '';
    this.selection = null;
    this.selectionData = null;
    Object.values(this.moveButtons).forEach((btn) => (btn.disabled = true));
    this.undoStack = [];
    this.redoStack = [];
    this.pushHistory();
    this.render();
    this.flashStatus(`Loaded map "${map.name}".`);
  }

  private exportMap(): void {
    const map = this.serializeCurrentMap();
    const blob = new Blob([JSON.stringify(map, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${map.name.replace(/\s+/g, '_') || 'map'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    this.flashStatus('Map exported.');
  }

  private importMap(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      file.text().then((text) => {
        try {
          const data = JSON.parse(text) as MapDefinition;
          if (!data.grid || !Array.isArray(data.grid)) {
            throw new Error('Invalid map structure.');
          }
          this.currentMapId = null; // Imported maps save as new entries
          this.grid = data.grid.map((row) => row.map((tile) => (tile === 'wall' ? 'wall' : 'empty')));
          this.spawns = (data.spawns || []).map((spawn) => ({ ...spawn }));
          this.goal = data.goal ? { ...data.goal } : null;
          this.props = (data.props || []).map((prop) => ({ ...prop, position: { ...prop.position } }));
          this.nameInput.value = data.name || 'Imported Map';
          this.authorInput.value = data.author || 'Unknown';
          this.difficultyInput.value = (data.difficultyTags || []).join(', ');
          this.descriptionInput.value = data.metadata?.description ?? '';
          this.scriptsInput.value = data.metadata?.scripts?.join('\n') ?? '';
          this.undoStack = [];
          this.redoStack = [];
          this.pushHistory();
          this.render();
          this.flashStatus('Map imported.');
        } catch (error) {
          console.error('Failed to import map', error);
          this.flashStatus('Import failed: invalid JSON.');
        }
      });
    };
    input.click();
  }

  private serializeCurrentMap(): MapDefinition {
    return {
      id: this.currentMapId ?? randomId(),
      name: this.nameInput.value || 'Untitled Map',
      author: this.authorInput.value || 'Unknown',
      difficultyTags: this.difficultyInput.value
        ? this.difficultyInput.value.split(',').map((tag) => tag.trim()).filter(Boolean)
        : [],
      grid: this.grid.map((row) => [...row]),
      spawns: this.spawns.map((spawn) => ({ ...spawn })),
      goal: this.goal ? { ...this.goal } : null,
      props: this.props.map((prop) => ({ ...prop, position: { ...prop.position } })),
      metadata: {
        description: this.descriptionInput.value,
        scripts: this.scriptsInput.value ? this.scriptsInput.value.split('\n').filter(Boolean) : [],
        createdAt: Date.now(),
        updatedAt: Date.now(),
      },
    };
  }

  private refreshSavedMaps(selectedId?: string): void {
    const maps = MapStorage.list();
    this.savedMapsSelect.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = '-- Saved Maps --';
    this.savedMapsSelect.appendChild(placeholder);
    maps.forEach((map) => {
      const option = document.createElement('option');
      option.value = map.id;
      option.textContent = map.name;
      this.savedMapsSelect.appendChild(option);
    });
    if (selectedId) {
      this.savedMapsSelect.value = selectedId;
    }
  }

  private simulateSpawn(type: string): void {
    this.previewPathsForMap();
    this.flashStatus(`Simulated ${type} spawn highlight.`);
  }

  private getTileFromPointer(event: PointerEvent): MapCoordinate | null {
    const rect = this.canvas.getBoundingClientRect();
    const scaleX = this.canvas.width / rect.width;
    const scaleY = this.canvas.height / rect.height;
    const x = Math.floor(((event.clientX - rect.left) * scaleX) / this.tileSize);
    const y = Math.floor(((event.clientY - rect.top) * scaleY) / this.tileSize);
    if (!this.isWithinGrid(x, y)) return null;
    return { x, y };
  }

  private isWithinGrid(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.gridSize && y < this.gridSize;
  }

  private createEmptyGrid(): MapTileType[][] {
    return Array.from({ length: this.gridSize }, () =>
      Array.from({ length: this.gridSize }, () => 'empty' as MapTileType)
    );
  }

  private render(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    for (let y = 0; y < this.gridSize; y++) {
      for (let x = 0; x < this.gridSize; x++) {
        const tile = this.grid[y][x];
        this.ctx.fillStyle = tile === 'wall' ? '#3d3d3d' : '#1a1a1a';
        this.ctx.fillRect(x * this.tileSize, y * this.tileSize, this.tileSize - 1, this.tileSize - 1);
      }
    }

    // Props
    this.props.forEach((prop) => {
      this.ctx.fillStyle = prop.type === 'tree' ? '#2e7d32' : '#8d6e63';
      this.ctx.beginPath();
      this.ctx.arc(
        prop.position.x * this.tileSize + this.tileSize / 2,
        prop.position.y * this.tileSize + this.tileSize / 2,
        this.tileSize / 4,
        0,
        Math.PI * 2
      );
      this.ctx.fill();
    });

    // Spawns
    this.spawns.forEach((spawn, index) => {
      this.ctx.fillStyle = '#4caf50';
      this.ctx.fillRect(spawn.x * this.tileSize, spawn.y * this.tileSize, this.tileSize - 1, this.tileSize - 1);
      this.ctx.fillStyle = '#000';
      this.ctx.font = `${Math.max(12, this.tileSize / 3)}px Courier`;
      this.ctx.fillText(
        `${index + 1}`,
        spawn.x * this.tileSize + this.tileSize / 2 - 4,
        spawn.y * this.tileSize + this.tileSize / 2 + 4
      );
    });

    // Goal
    if (this.goal) {
      this.ctx.fillStyle = '#c62828';
      this.ctx.fillRect(this.goal.x * this.tileSize, this.goal.y * this.tileSize, this.tileSize - 1, this.tileSize - 1);
    }

    // Selection overlay
    if (this.selection) {
      this.ctx.strokeStyle = '#ffeb3b';
      this.ctx.lineWidth = 2;
      this.ctx.strokeRect(
        this.selection.x * this.tileSize,
        this.selection.y * this.tileSize,
        this.selection.width * this.tileSize,
        this.selection.height * this.tileSize
      );
    }

    // Path preview
    if (this.previewPaths.length > 0) {
      this.ctx.lineWidth = 3;
      this.previewPaths.forEach((path, idx) => {
        this.ctx.strokeStyle = idx % 2 === 0 ? '#00ff90' : '#00bcd4';
        this.ctx.beginPath();
        path.forEach((point, index) => {
          const px = point.x;
          const py = point.y;
          if (index === 0) {
            this.ctx.moveTo(px, py);
          } else {
            this.ctx.lineTo(px, py);
          }
        });
        this.ctx.stroke();
      });
    }
  }

  private pushHistory(): void {
    const snapshot: MapSnapshot = {
      grid: this.grid.map((row) => [...row]),
      spawns: this.spawns.map((spawn) => ({ ...spawn })),
      goal: this.goal ? { ...this.goal } : null,
      props: this.props.map((prop) => ({ ...prop, position: { ...prop.position } })),
      metadata: {
        description: this.descriptionInput.value,
        scripts: this.scriptsInput.value ? this.scriptsInput.value.split('\n').filter(Boolean) : [],
        createdAt: Date.now(),
        updatedAt: Date.now(),
      },
      name: this.nameInput.value,
      author: this.authorInput.value,
      difficultyTags: this.difficultyInput.value,
    };
    this.undoStack.push(snapshot);
    if (this.undoStack.length > this.historyLimit) {
      this.undoStack.shift();
    }
    this.redoStack = [];
  }

  private flashStatus(message: string): void {
    this.statusBar.textContent = message;
  }

  private testMap(): void {
    const map = this.getCurrentMap();
    if (!map.goal || map.spawns.length === 0) {
      this.flashStatus('Error: Map must have at least one spawn and a goal.');
      return;
    }
    
    // Validate path by running preview
    this.previewPathsForMap();
    // Check if preview paths were generated (stored in this.previewPaths)
    if (!this.previewPaths || this.previewPaths.length === 0) {
      this.flashStatus('Error: No valid path found. Cannot test map.');
      return;
    }
    
    this.hide();
    this.options.onTestMap?.(map);
  }

  private getCurrentMap(): MapDefinition {
    return {
      id: this.currentMapId ?? randomId(),
      name: this.nameInput.value || 'Unnamed Map',
      author: this.authorInput.value || 'Unknown',
      difficultyTags: this.difficultyInput.value.split(',').map(s => s.trim()).filter(Boolean),
      grid: this.grid,
      spawns: this.spawns,
      goal: this.goal,
      props: this.props,
      metadata: {
        description: this.descriptionInput.value || undefined,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        scripts: this.scriptsInput.value ? this.scriptsInput.value.split('\n').filter(Boolean) : undefined,
      },
    };
  }
}

